/**
 * Simple logging service with log levels.
 *
 * In production, only WARN and ERROR are output.
 * In development, all levels are output.
 *
 * This provides a consistent logging interface that can be extended
 * later to send logs to an external service if needed.
 */

type LogLevel = "debug" | "info" | "warn" | "error";

const LOG_LEVELS: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
};

const currentLevel: LogLevel = import.meta.env.MODE === "production" ? "warn" : "debug";

function shouldLog(level: LogLevel): boolean {
  return LOG_LEVELS[level] >= LOG_LEVELS[currentLevel];
}

/** Log a debug message (dev only). */
function debug(message: string, ...args: unknown[]): void {
  if (shouldLog("debug")) {
    console.debug(`[DEBUG] ${message}`, ...args);
  }
}

/** Log an informational message (dev only). */
function info(message: string, ...args: unknown[]): void {
  if (shouldLog("info")) {
    console.info(`[INFO] ${message}`, ...args);
  }
}

/** Log a warning message. */
function warn(message: string, ...args: unknown[]): void {
  if (shouldLog("warn")) {
    console.warn(`[WARN] ${message}`, ...args);
  }
}

/** Log an error message. */
function error(message: string, ...args: unknown[]): void {
  if (shouldLog("error")) {
    console.error(`[ERROR] ${message}`, ...args);
  }
}

export const logger = { debug, info, warn, error };
